import { useState } from "react";
import { Building2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  anyWorkspaceToken,
  storeWorkspaces,
  tokenForWorkspace,
  type WorkspaceSession,
  type WorkspaceSummary,
} from "../lib/workspaces";

interface WorkspacePickerProps {
  workspaces: WorkspaceSummary[];
  selectionToken?: string;
  title?: string;
}

export function WorkspacePicker({ workspaces, selectionToken, title = "Choose a workspace" }: WorkspacePickerProps) {
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function choose(workspace: WorkspaceSummary) {
    if (!workspace.isActive) return;
    setSwitching(workspace.id);
    setError(null);

    const existingToken = tokenForWorkspace(workspace.id);
    if (existingToken) {
      navigate(`/w/${workspace.slug}/${workspaces.length > 1 ? "all-sites" : "checkins"}`, { replace: true });
      return;
    }

    try {
      const response = await fetch("/api/dashboard/auth/switch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${selectionToken ?? anyWorkspaceToken()}`,
        },
        body: JSON.stringify({ tenantId: workspace.id }),
      });
      const result = await response.json();
      if (!result.success || !result.data) {
        setError(result.error?.message || "Could not open this workspace");
        setSwitching(null);
        return;
      }
      storeWorkspaces(workspaces);
      login(result.data as WorkspaceSession);
      refresh();
      sessionStorage.removeItem("vms_selection_token");
      sessionStorage.removeItem("vms_selection_workspaces");
      navigate(`/w/${workspace.slug}/${workspaces.length > 1 ? "all-sites" : "checkins"}`, { replace: true });
    } catch {
      setError("Network error");
      setSwitching(null);
    }
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <div className="mb-5 flex items-center justify-center gap-2">
            <img src="/singvms-seal.svg" alt="SingVMS" className="h-9 w-9" />
            <span className="font-display text-xl font-bold text-stone-900">SingVMS</span>
          </div>
          <h1 className="font-display text-2xl font-semibold text-stone-900">{title}</h1>
          <p className="mt-2 text-sm text-stone-500">Select the company you want to manage.</p>
        </div>
        {error && <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
        <div className="grid gap-3 sm:grid-cols-2">
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              disabled={!workspace.isActive || switching !== null}
              onClick={() => choose(workspace)}
              className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:border-brand-300 hover:shadow disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-stone-100">
                {workspace.logoUrl ? (
                  <img src={workspace.logoUrl} alt="" className="h-full w-full object-contain" />
                ) : (
                  <Building2 className="h-5 w-5 text-stone-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-stone-900">{workspace.name}</p>
                <p className="mt-0.5 text-xs capitalize text-stone-500">
                  {workspace.isActive ? workspace.role.toLowerCase() : "Inactive"}
                </p>
              </div>
              {switching === workspace.id && <Loader2 className="h-4 w-4 animate-spin text-brand-500" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
